/**
 * SYR-107 — the element explainer video.
 *
 * Only ever rendered when this page is STANDALONE. Inside the iframe on
 * brandscience.ai a modal is a trap: it is positioned against the frame's own
 * viewport, which is sized to the content and may be a few hundred pixels
 * tall and scrolled halfway out of view. The video would open somewhere the
 * reader is not looking, at a size nobody can watch. When embedded, the page
 * asks the PARENT to open it instead, over the full window.
 *
 * The YouTube iframe is created only when the modal opens, and destroyed with
 * it: mounting it up front would load YouTube's player on every read-out, for
 * a video most readers never play.
 */

import React from 'react';
import { Modal } from 'antd';

interface Props {
    videoId: string | null;
    /** Resolved server-side, so the wording is editable without a deploy. */
    title: string | null;
    onClose: () => void;
}

export const ElementVideoModal: React.FC<Props> = ({ videoId, title, onClose }) => (
    <Modal
        open={!!videoId}
        onCancel={onClose}
        footer={null}
        width={880}
        centered
        destroyOnClose
        title={title || undefined}
        styles={{ body: { padding: 0 } }}
    >
        {videoId ? (
            <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                <iframe
                    // nocookie: this plays on a public marketing surface, and
                    // the standard domain sets tracking cookies before anyone
                    // presses play, which our own consent banner then has to
                    // answer for.
                    src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                    title={title ?? 'Element explainer'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        border: 0,
                        borderRadius: '0 0 8px 8px',
                    }}
                />
            </div>
        ) : null}
    </Modal>
);
